/** Company weekly working hours stored on users/{companyId}.workingHours */

export type WeekdayKey =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export interface CompanyDayHours {
  isWorking: boolean;
  /** When true, company is open all day; start/end are ignored for booking. */
  is24Hours: boolean;
  start: string;
  end: string;
}

export type CompanyWorkingHours = Record<WeekdayKey, CompanyDayHours>;

export const WEEKDAY_KEYS: WeekdayKey[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

/**
 * Default company hours:
 * - Sat–Thu: 10:00 AM → 10:00 PM
 * - Friday: 2:00 PM → 12:00 AM (midnight)
 */
export function getDefaultCompanyWorkingHours(): CompanyWorkingHours {
  const weekday: CompanyDayHours = {
    isWorking: true,
    is24Hours: false,
    start: '10:00 AM',
    end: '10:00 PM',
  };
  const friday: CompanyDayHours = {
    isWorking: true,
    is24Hours: false,
    start: '2:00 PM',
    end: '12:00 AM',
  };

  return {
    sunday: { ...weekday },
    monday: { ...weekday },
    tuesday: { ...weekday },
    wednesday: { ...weekday },
    thursday: { ...weekday },
    friday: { ...friday },
    saturday: { ...weekday },
  };
}

export function normalizeCompanyDayHours(
  raw: Partial<CompanyDayHours> | null | undefined,
  fallback?: CompanyDayHours
): CompanyDayHours {
  const base = fallback || {
    isWorking: true,
    is24Hours: false,
    start: '10:00 AM',
    end: '10:00 PM',
  };
  return {
    isWorking: raw?.isWorking === undefined ? base.isWorking : !!raw.isWorking,
    is24Hours: !!raw?.is24Hours,
    start: (raw?.start || base.start || '10:00 AM').toString(),
    end: (raw?.end || base.end || '10:00 PM').toString(),
  };
}

/** Merge stored hours with defaults so missing days are filled. */
export function mergeCompanyWorkingHours(
  stored: Partial<Record<WeekdayKey, Partial<CompanyDayHours>>> | null | undefined
): CompanyWorkingHours {
  const defaults = getDefaultCompanyWorkingHours();
  const out = {} as CompanyWorkingHours;
  for (const day of WEEKDAY_KEYS) {
    out[day] = normalizeCompanyDayHours(stored?.[day], defaults[day]);
  }
  return out;
}

/** Payload ready to write to Firestore. */
export function serializeCompanyWorkingHours(
  hours: CompanyWorkingHours
): CompanyWorkingHours {
  const out = {} as CompanyWorkingHours;
  for (const day of WEEKDAY_KEYS) {
    const d = hours[day];
    out[day] = {
      isWorking: !!d?.isWorking,
      is24Hours: !!d?.is24Hours,
      start: d?.is24Hours ? '12:00 AM' : (d?.start || '10:00 AM'),
      end: d?.is24Hours ? '11:59 PM' : (d?.end || '10:00 PM'),
    };
  }
  return out;
}

/** Default fulfillment lead time in minutes when company has no override. */
export const DEFAULT_FULFILLMENT_LEAD_MINUTES = 90;

export function weekdayKeyFromDate(date: Date = new Date()): WeekdayKey {
  return WEEKDAY_KEYS[date.getDay()];
}

/** Parse labels like "10:00 AM", "12:00 AM", "14:30". */
export function parseWorkingTimeToMinutes(label: string | null | undefined): number | null {
  const t = (label ?? '').trim();
  if (!t) {
    return null;
  }

  const ampm = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampm) {
    let h = Number(ampm[1]);
    const m = Number(ampm[2]);
    const period = ampm[3].toUpperCase();
    if (Number.isNaN(h) || Number.isNaN(m) || m < 0 || m > 59 || h < 1 || h > 12) {
      return null;
    }
    if (period === 'AM') {
      if (h === 12) h = 0;
    } else if (h !== 12) {
      h += 12;
    }
    return h * 60 + m;
  }

  const h24 = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (h24) {
    const h = Number(h24[1]);
    const m = Number(h24[2]);
    if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
      return null;
    }
    return h * 60 + m;
  }

  return null;
}

function atMinutesOnDay(day: Date, minutesFromMidnight: number): Date {
  const d = new Date(day);
  d.setHours(0, 0, 0, 0);
  d.setMinutes(minutesFromMidnight);
  return d;
}

/**
 * Resolve open window for a calendar day.
 * Overnight ranges (end <= start, e.g. 2:00 PM → 12:00 AM) end on the next calendar day.
 */
export function getWorkingWindowForDate(
  day: Date,
  hours: CompanyDayHours | null | undefined
): { start: Date; end: Date } | null {
  if (!hours?.isWorking) {
    return null;
  }

  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);

  if (hours.is24Hours) {
    const end = new Date(dayStart);
    end.setDate(end.getDate() + 1);
    return { start: dayStart, end };
  }

  const startMin = parseWorkingTimeToMinutes(hours.start);
  const endMin = parseWorkingTimeToMinutes(hours.end);
  if (startMin == null || endMin == null) {
    return null;
  }

  const start = atMinutesOnDay(dayStart, startMin);
  let end = atMinutesOnDay(dayStart, endMin);
  if (endMin === 0 || end.getTime() <= start.getTime()) {
    end = new Date(end);
    end.setDate(end.getDate() + 1);
  }
  return { start, end };
}

export function getFulfillmentLeadMinutes(company: any): number {
  const raw =
    company?.default_fulfillment_lead_time ??
    company?.defaultFulfillmentLeadTime ??
    company?.fulfillmentLeadTimeMinutes;
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 0) {
    return Math.round(n);
  }
  return DEFAULT_FULFILLMENT_LEAD_MINUTES;
}

export function isCompanyAcceptingProductOrders(
  company: any,
  now: Date = new Date()
): boolean {
  if (!company || company.storefrontOffline === true) {
    return false;
  }

  const hours = mergeCompanyWorkingHours(company.workingHours);
  const todayKey = weekdayKeyFromDate(now);
  const today = hours[todayKey];
  const window = getWorkingWindowForDate(now, today);
  if (!window) {
    return false;
  }

  if (now.getTime() < window.start.getTime() || now.getTime() >= window.end.getTime()) {
    return false;
  }

  if (today.is24Hours) {
    return true;
  }

  const lead = getFulfillmentLeadMinutes(company);
  const minutesLeft = (window.end.getTime() - now.getTime()) / 60000;
  return minutesLeft >= lead;
}

export interface ScheduledArrivalResult {
  scheduledAt: Date;
  rolledToNextDay: boolean;
  leadMinutes: number;
  label: string;
}

function formatArrivalLabel(date: Date): string {
  try {
    return date.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return date.toISOString();
  }
}

export function calculateScheduledArrivalTime(
  company: any,
  now: Date = new Date()
): ScheduledArrivalResult {
  const hours = mergeCompanyWorkingHours(company?.workingHours);
  const lead = getFulfillmentLeadMinutes(company);
  const candidate = new Date(now.getTime() + lead * 60000);

  const todayKey = weekdayKeyFromDate(now);
  const todayWindow = getWorkingWindowForDate(now, hours[todayKey]);

  if (
    todayWindow &&
    candidate.getTime() >= todayWindow.start.getTime() &&
    candidate.getTime() <= todayWindow.end.getTime()
  ) {
    return {
      scheduledAt: candidate,
      rolledToNextDay: false,
      leadMinutes: lead,
      label: formatArrivalLabel(candidate),
    };
  }

  for (let i = 1; i <= 14; i++) {
    const day = new Date(now);
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() + i);
    const key = weekdayKeyFromDate(day);
    const window = getWorkingWindowForDate(day, hours[key]);
    if (!window) {
      continue;
    }
    const arrival = new Date(window.start.getTime() + lead * 60000);
    return {
      scheduledAt: arrival,
      rolledToNextDay: true,
      leadMinutes: lead,
      label: formatArrivalLabel(arrival),
    };
  }

  return {
    scheduledAt: candidate,
    rolledToNextDay: false,
    leadMinutes: lead,
    label: formatArrivalLabel(candidate),
  };
}

export type CarCareScheduleWindow =
  | 'few_hours_today'
  | 'another_date'
  | 'tomorrow'
  | 'this_weekend'
  | 'custom_date';

export type ScheduleDayPeriod = 'morning' | 'afternoon' | 'evening';

export function getScheduleDayPeriodLabel(
  period: ScheduleDayPeriod | string | null | undefined
): string {
  const map: Record<string, string> = {
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
  };
  return map[String(period || '')] || String(period || '');
}

export function getCarCareScheduleWindowLabel(
  window: CarCareScheduleWindow | string | null | undefined
): string {
  const map: Record<string, string> = {
    few_hours_today: 'Within a few hours today',
    another_date: 'Schedule for another date',
    tomorrow: 'Tomorrow',
    this_weekend: 'This weekend',
    custom_date: 'Specific date',
  };
  return map[String(window || '')] || String(window || '');
}

export function buildSchedulePreferenceLabel(opts: {
  window: CarCareScheduleWindow | string | null | undefined;
  requestedDate?: Date | null;
  dayPeriod?: ScheduleDayPeriod | string | null;
}): string {
  const window = opts.window;
  if (!window || window === 'few_hours_today') {
    return getCarCareScheduleWindowLabel('few_hours_today');
  }
  const parts: string[] = [];
  if (opts.requestedDate) {
    try {
      parts.push(
        opts.requestedDate.toLocaleDateString(undefined, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
      );
    } catch {
      /* ignore */
    }
  }
  const periodLabel = getScheduleDayPeriodLabel(opts.dayPeriod);
  if (periodLabel) parts.push(periodLabel);
  return parts.length
    ? parts.join(' · ')
    : getCarCareScheduleWindowLabel(window);
}

export function isConfirmTimeWithinCompanyHours(
  company: any,
  serviceDate: Date,
  timeLabel: string
): { ok: boolean; message?: string } {
  const hours = mergeCompanyWorkingHours(company?.workingHours);
  const dayKey = weekdayKeyFromDate(serviceDate);
  const dayHours = hours[dayKey];

  if (!dayHours?.isWorking) {
    return { ok: false, message: 'Company is closed on this day.' };
  }
  if (dayHours.is24Hours) {
    return { ok: true };
  }

  const minutes = parseWorkingTimeToMinutes(timeLabel);
  if (minutes == null) {
    return { ok: false, message: 'Invalid time.' };
  }

  const openWindow = getWorkingWindowForDate(serviceDate, dayHours);
  if (!openWindow) {
    return { ok: false, message: 'Working hours are not configured for this day.' };
  }

  const candidate = new Date(serviceDate);
  candidate.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);

  if (
    candidate.getTime() < openWindow.start.getTime() ||
    candidate.getTime() >= openWindow.end.getTime()
  ) {
    return {
      ok: false,
      message: `Time must be between ${dayHours.start} and ${dayHours.end}.`,
    };
  }
  return { ok: true };
}

export function combineDateAndTimeLabel(
  date: Date,
  timeLabel: string
): Date | null {
  const minutes = parseWorkingTimeToMinutes(timeLabel);
  if (minutes == null) {
    return null;
  }
  const d = new Date(date);
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d;
}
